/**
 * Resource policy tab on the connection detail view: one row per QIE channel,
 * joined to the Engage sync policy for its resource type, with a switch that
 * turns sync on or off. Turning sync off does not stop the channel; QIE reads
 * the policy on each poll and skips the resource.
 */
import {
  Box,
  Button,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useUpsertEhrSyncResource } from '@hooks/api/ehr-sync';
import { useSnackbar } from '@hooks/useSnackbar';

import type { EhrSyncResource } from '@models/ehr-sync';

import { formatDate } from '@utils/format';

import ResourceStateChip from '../../components/ResourceStateChip';
import { toneTextColor, toneTintColor } from '../../components/tone';
import { directionMessages } from '../../messages';
import { DIRECTION_GLYPH } from '../shared/monitoring.util';

const messages = defineMessages({
  title: {
    id: 'ehrSync.detail.policy.title',
    defaultMessage: 'Resource policy',
  },
  intro: {
    id: 'ehrSync.detail.policy.intro',
    defaultMessage:
      "One row per QIE channel in this zone. The channel's build in QIE sets its resource type and direction. Turning sync off tells QIE to skip that resource on its next poll. The channel itself keeps running.",
  },
  resourceType: {
    id: 'ehrSync.detail.policy.column.resourceType',
    defaultMessage: 'Resource type',
  },
  channel: {
    id: 'ehrSync.detail.policy.column.channel',
    defaultMessage: 'QIE channel',
  },
  direction: {
    id: 'ehrSync.detail.policy.column.direction',
    defaultMessage: 'Direction',
  },
  state: {
    id: 'ehrSync.detail.policy.column.state',
    defaultMessage: 'Channel state',
  },
  enabled: {
    id: 'ehrSync.detail.policy.column.enabled',
    defaultMessage: 'Sync enabled',
  },
  lastChanged: {
    id: 'ehrSync.detail.policy.column.lastChanged',
    defaultMessage: 'Last changed',
  },
  newChannel: {
    id: 'ehrSync.detail.policy.newChannel',
    defaultMessage: 'New channel in QIE. Off until someone enables it.',
  },
  notFound: {
    id: 'ehrSync.detail.policy.notFound',
    defaultMessage: 'Not found in QIE',
  },
  notFoundNote: {
    id: 'ehrSync.detail.policy.notFoundNote',
    defaultMessage:
      'Policy kept. It applies again if a channel with this name comes back.',
  },
  missing: {
    id: 'ehrSync.detail.policy.missing',
    defaultMessage: 'Missing',
  },
  notConfigured: {
    id: 'ehrSync.detail.policy.notConfigured',
    defaultMessage: 'Not configured',
  },
  footer: {
    id: 'ehrSync.detail.policy.footer',
    defaultMessage:
      'Channels are added, removed and rebuilt in the QIE console. Engage stores only whether each one syncs.',
  },
  switchLabel: {
    id: 'ehrSync.detail.policy.switchLabel',
    defaultMessage: 'Sync {resourceType}',
  },
  switchLabelChannel: {
    id: 'ehrSync.detail.policy.switchLabelChannel',
    defaultMessage: 'Sync {resourceType} ({channel})',
  },
  turnedOn: {
    id: 'ehrSync.detail.policy.turnedOn',
    defaultMessage: '{resourceType} sync turned on',
  },
  turnedOff: {
    id: 'ehrSync.detail.policy.turnedOff',
    defaultMessage: '{resourceType} sync turned off',
  },
  undo: {
    id: 'ehrSync.detail.policy.undo',
    defaultMessage: 'Undo',
  },
});

/** Props for {@link ResourcePolicyTab}. */
interface ResourcePolicyTabProps {
  /** The connection's registry row. */
  ehrConnectionId: string;
  resources: EhrSyncResource[];
}

/**
 * Policy table for one connection.
 *
 * @param props - see {@link ResourcePolicyTabProps}
 */
export default function ResourcePolicyTab({
  ehrConnectionId,
  resources,
}: ResourcePolicyTabProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [setEnabled] = useUpsertEhrSyncResource();
  // Resource types with a write in flight, so a double click or an Undo cannot
  // race a toggle. The ref is what writes check; the state re-renders switches.
  const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());
  const savingRef = useRef(new Set<string>());

  const write = async (resourceType: string, enabled: boolean) => {
    if (savingRef.current.has(resourceType)) {
      return false;
    }
    savingRef.current.add(resourceType);
    setSaving(new Set(savingRef.current));
    try {
      return await setEnabled({ ehrConnectionId, resourceType, enabled });
    } finally {
      savingRef.current.delete(resourceType);
      setSaving(new Set(savingRef.current));
    }
  };

  const undo = async (resourceType: string, enabled: boolean) => {
    const saved = await write(resourceType, enabled);
    if (saved) {
      enqueueSnackbar({
        variant: 'info',
        message: formatMessage(
          enabled ? messages.turnedOn : messages.turnedOff,
          { resourceType }
        ),
      });
    }
  };

  const toggle = async (resource: EhrSyncResource, enabled: boolean) => {
    const saved = await write(resource.resourceType, enabled);
    if (!saved) {
      return;
    }
    const values = { resourceType: resource.resourceType };
    enqueueSnackbar({
      variant: 'success',
      message: (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <span>
            {formatMessage(
              enabled ? messages.turnedOn : messages.turnedOff,
              values
            )}
          </span>
          <UndoButton
            label={formatMessage(messages.undo)}
            onUndo={() => undo(resource.resourceType, !enabled)}
          />
        </Stack>
      ),
    });
  };

  const headers = [
    messages.resourceType,
    messages.channel,
    messages.direction,
    messages.state,
    messages.enabled,
    messages.lastChanged,
  ];

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          {formatMessage(messages.title)}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {formatMessage(messages.intro)}
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header.id}>
                  <Typography
                    variant="overline"
                    fontWeight="bold"
                    color="text.secondary"
                    lineHeight={1.5}
                  >
                    {formatMessage(header)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map((resource) => {
              const missing = resource.policyState === 'channelNotFound';
              return (
                <TableRow
                  key={resource.channelId ?? `policy-${resource.resourceType}`}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="text.primary"
                    >
                      {resource.resourceType}
                    </Typography>
                    {resource.policyState === 'noPolicy' ? (
                      <Typography variant="caption" color="text.secondary">
                        {formatMessage(messages.newChannel)}
                      </Typography>
                    ) : null}
                    {missing ? (
                      <Stack>
                        <Typography
                          variant="caption"
                          color="error.main"
                          fontWeight="bold"
                        >
                          {formatMessage(messages.notFound)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMessage(messages.notFoundNote)}
                        </Typography>
                      </Stack>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {resource.channelName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {resource.direction ? (
                        <>
                          <Box
                            component="span"
                            aria-hidden
                            sx={{ color: 'primary.main' }}
                          >
                            {DIRECTION_GLYPH[resource.direction]}
                          </Box>{' '}
                          {formatMessage(directionMessages[resource.direction])}
                        </>
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {resource.state ? (
                      <ResourceStateChip state={resource.state} />
                    ) : (
                      <Typography
                        component="span"
                        variant="caption"
                        fontWeight="bold"
                        sx={(theme) => ({
                          display: 'inline-flex',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: toneTextColor(theme, 'error'),
                          border: 1,
                          borderColor: alpha(
                            toneTintColor(theme, 'error'),
                            0.5
                          ),
                          borderRadius: 0.75,
                          px: 1,
                          py: 0.25,
                        })}
                      >
                        {formatMessage(messages.missing)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={resource.enabled}
                      disabled={missing || saving.has(resource.resourceType)}
                      onChange={(_event, checked) => {
                        void toggle(resource, checked);
                      }}
                      inputProps={{
                        // The policy is per resource type, so every channel of a
                        // type shares one switch value; the label tells them apart.
                        'aria-label':
                          resource.channelName === resource.resourceType
                            ? formatMessage(messages.switchLabel, {
                                resourceType: resource.resourceType,
                              })
                            : formatMessage(messages.switchLabelChannel, {
                                resourceType: resource.resourceType,
                                channel: resource.channelName,
                              }),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {resource.policyUpdatedAt
                        ? formatDate({
                            date: resource.policyUpdatedAt,
                            formatter: 'datetime',
                          })
                        : formatMessage(messages.notConfigured)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}
      >
        {formatMessage(messages.footer)}
      </Typography>
    </Paper>
  );
}

/** Props for {@link UndoButton}. */
interface UndoButtonProps {
  label: string;
  onUndo: () => Promise<void>;
}

/**
 * The snackbar's Undo action. It disables itself after one click, since the
 * snackbar stays up until it times out.
 *
 * @param props - see {@link UndoButtonProps}
 */
function UndoButton({ label, onUndo }: UndoButtonProps) {
  const [used, setUsed] = useState(false);
  return (
    <Button
      size="small"
      color="inherit"
      disabled={used}
      onClick={() => {
        setUsed(true);
        void onUndo();
      }}
      sx={{ fontWeight: 'bold' }}
    >
      {label}
    </Button>
  );
}
